import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import supabase from 'modules/core/supabase/supabase';
import { mapVideoMarkerDraftToDto, mapVideoMarkerDto } from 'modules/video-markers/domain/data-mappers';
import {
  NativeMarkerResult,
  RecorderClock,
  RecorderConfig,
  RecorderConnection,
  RecorderKind,
  RecorderPosition,
  VideoMarkerDraft,
  VideoMarkerKind,
} from 'modules/video-markers/domain/interfaces';
import { createRecorderClock } from 'modules/video-markers/domain/repositories';
import {
  buildClockMarker,
  buildEventMarker,
  buildManualMarker,
  ClockAnchor,
  ClockAnchorTracker,
  EventVideoPosition,
  MatchClockReading,
  NewEventsTracker,
  parseRecorderFragment,
  recorderConfigToFragment,
  resolveEventVideoPosition,
  VideoMarkerContext,
  VideoTimeline,
} from 'modules/video-markers/domain/utils';
import { MatchEvent, MatchEventType } from 'shared/types/match-event';

/** Как часто панель перечитывает позицию записи и эфира. */
const POSITION_POLL_MS = 1_000;
/** Как часто трекер опорных точек проверяет, не остановились ли игровые часы. */
const ANCHOR_POLL_MS = 1_000;
/** Подпись нативной метки (глава OBS, строка лога vMix) у ручной метки и хоткея оператора. */
const MANUAL_NATIVE_LABEL = 'MANUAL';

const IDLE: RecorderConnection = { status: 'idle' };

export interface UseVideoMarkersParams {
  /** `location.hash` оверлея: настройка подключения из «Устройств» (`recorder-config-fragment.ts`). */
  hash: string;
  matchId?: number;
  /** Организация: `tournamentId` бэкенда. */
  organizerId?: number;
  deviceId?: number;
  /** Подмена фабрики часов в тестах. */
  createClock?: (config: RecorderConfig) => RecorderClock;
}

/** Последнее помеченное событие: где оно в видео по хронометражу и по получению. */
export interface LastEventVideoPosition {
  eventType: MatchEventType | null;
  timeline: VideoTimeline;
  position: EventVideoPosition;
}

export interface VideoMarkersState {
  /** `null` — в ссылке нет настройки, видеометки выключены. */
  recorder: RecorderKind | null;
  connection: RecorderConnection;
  /** Свежая позиция программы; `null` — нет подключения или программа не ответила. */
  position: RecorderPosition | null;
  /** Строки этой программы за текущий матч — прочитанные из таблицы и записанные сейчас. */
  counts: Record<VideoMarkerKind, number>;
  lastEvent: LastEventVideoPosition | null;
  /** Ручная метка — кнопка «⭐» в панели; хоткей оператора ведёт сюда же. */
  markManual: () => Promise<void>;
}

type BuildDraft = (context: VideoMarkerContext, nativeMarker: NativeMarkerResult | null) => VideoMarkerDraft;

/**
 * Конвейер видеометок оверлея (IMGM-1484).
 *
 * По настройке из `#`-фрагмента подключается к OBS или vMix и, пока открыт матч, пишет в
 * `video_markers` три вида строк: `event` — новое событие журнала и позиция программы в момент,
 * когда оно дошло до оверлея; `clock` — опорные точки «хронометраж ↔ позиция»
 * (`ClockAnchorTracker`); `manual` — кнопка «⭐» и хоткей оператора в самой программе.
 *
 * Своя подписка на журнал, а не путь `use-init-subscribe-overlay-v2`: при закреплённом табло тот
 * новые события в стор не кладёт. Всё, что было в журнале при открытии матча, — базовая линия:
 * момент получения для этих событий уже прошёл.
 *
 * Без фрагмента хук ничего не делает: ни подключения, ни подписок.
 */
export function useVideoMarkers({
  hash,
  matchId,
  organizerId,
  deviceId,
  createClock = createRecorderClock,
}: UseVideoMarkersParams): VideoMarkersState {
  const config = useMemo(() => parseRecorderFragment(hash), [hash]);
  // Строковый ключ, чтобы новый объект настройки на тот же фрагмент не пересоздавал часы.
  const configKey = config ? recorderConfigToFragment(config) : null;

  const [clock, setClock] = useState<RecorderClock | null>(null);
  const [connection, setConnection] = useState<RecorderConnection>(IDLE);
  const [position, setPosition] = useState<RecorderPosition | null>(null);
  const [markers, setMarkers] = useState<VideoMarkerDraft[]>([]);
  const markManualRef = useRef<() => Promise<void>>(async () => {});

  // Часы программы живут, пока в ссылке та же настройка.
  useEffect(() => {
    if (configKey === null) return;

    const next = createClock(parseRecorderFragment(configKey)!);
    setClock(next);
    setConnection(next.connection.value);
    const unsubscribe = next.connection.subscribe((value) => setConnection(value));
    next.connect().catch((error) => reportError('Не удалось подключиться к программе записи', error));

    return () => {
      unsubscribe();
      next.disconnect();
      setClock(null);
      setConnection(IDLE);
    };
  }, [configKey, createClock]);

  const isConnected = connection.status === 'connected';

  useEffect(() => {
    if (!clock || !isConnected) {
      setPosition(null);
      return;
    }

    let cancelled = false;
    const refresh = () =>
      clock.getPosition().then(
        (next) => !cancelled && setPosition(next),
        () => !cancelled && setPosition(null),
      );

    void refresh();
    const timer = setInterval(refresh, POSITION_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [clock, isConnected]);

  // Конвейер одного матча: смена матча или программы начинает всё с чистого листа.
  useEffect(() => {
    if (!clock || matchId === undefined) return;

    let cancelled = false;
    const chronology = supabase.matchChronology;
    const recorder = clock.kind;
    const newEvents = new NewEventsTracker();
    const anchors = new ClockAnchorTracker();
    let lastReading: MatchClockReading | null = null;
    let unsubscribeEvents = () => {};

    setMarkers([]);

    // Позицию спрашиваем первой: от неё считается метка, нативная метка и запись подождут.
    const record = async (build: BuildDraft, nativeLabel: string | null) => {
      if (clock.connection.value.status !== 'connected') return;

      const receivedAt = new Date();
      const chronologyCommitAt = chronology.lastCommitAt.value;

      try {
        const recorderPosition = await clock.getPosition();
        const nativeMarker =
          nativeLabel === null ? null : await clock.writeNativeMarker(nativeLabel).catch((): NativeMarkerResult => 'failed');
        const context: VideoMarkerContext = {
          organizerId: organizerId ?? null,
          matchId,
          deviceId: deviceId ?? null,
          recorder,
          position: recorderPosition,
          receivedAt,
          chronologyCommitAt,
        };
        const draft = build(context, nativeMarker);

        await supabase.videoMarkers.upsert(mapVideoMarkerDraftToDto(draft));
        if (!cancelled) setMarkers((previous) => [...previous, draft]);
      } catch (error) {
        reportError('Не удалось записать видеометку', error);
      }
    };

    const recordAnchor = (anchor: ClockAnchor | null) => {
      if (anchor) void record((context) => buildClockMarker(anchor, context), null);
    };

    const onEvents = (events: MatchEvent[]) => {
      for (const event of newEvents.take(events)) {
        void record((context, native) => buildEventMarker(event, context, native), `${event.type} #${event.id}`);
      }
    };

    // Подписка только на изменения: текущее значение может остаться от прошлого матча.
    const unsubscribeTime = chronology.currentTime.subscribe(({ period, seconds }) => {
      lastReading = { period, seconds };
      recordAnchor(anchors.observe(lastReading, Date.now()));
    });
    const anchorTimer = setInterval(() => recordAnchor(anchors.poll(Date.now())), ANCHOR_POLL_MS);

    markManualRef.current = () => record((context, native) => buildManualMarker(lastReading, context, native), MANUAL_NATIVE_LABEL);
    // Слушатель хоткея не должен бросать: у vMix исключение подписчика роняет опрос.
    const unsubscribeOperator = clock.onOperatorMarker(() => void markManualRef.current());

    // Базовая линия — строка журнала, прочитанная заново: `events` до первого обновления нового
    // матча хранит журнал прошлого. Обновления, пришедшие за время чтения, тоже в базовой линии.
    void chronology.getRow(matchId).then((row) => {
      if (cancelled) return;
      newEvents.take([...((row?.chronology ?? []) as MatchEvent[]), ...chronology.events.value]);
      unsubscribeEvents = chronology.events.subscribe(onEvents);
    });

    supabase.videoMarkers.listByMatch(matchId).then(
      (rows) => {
        if (cancelled) return;
        const saved = rows.map(mapVideoMarkerDto).filter((row) => row.recorder === recorder);
        setMarkers((previous) => [...saved, ...previous]);
      },
      (error) => reportError('Не удалось прочитать видеометки матча', error),
    );

    return () => {
      cancelled = true;
      unsubscribeEvents();
      unsubscribeTime();
      clearInterval(anchorTimer);
      unsubscribeOperator();
      markManualRef.current = async () => {};
    };
  }, [clock, matchId, organizerId, deviceId]);

  const markManual = useCallback(() => markManualRef.current(), []);
  const counts = useMemo(() => countByKind(markers), [markers]);
  const lastEvent = useMemo(() => findLastEventPosition(markers), [markers]);

  const recorder = config?.recorder ?? null;

  return useMemo(
    () => ({ recorder, connection, position, counts, lastEvent, markManual }),
    [recorder, connection, position, counts, lastEvent, markManual],
  );
}

function countByKind(markers: readonly VideoMarkerDraft[]): Record<VideoMarkerKind, number> {
  const counts: Record<VideoMarkerKind, number> = { event: 0, manual: 0, clock: 0 };
  for (const marker of markers) counts[marker.kind] += 1;
  return counts;
}

function findLastEventPosition(markers: readonly VideoMarkerDraft[]): LastEventVideoPosition | null {
  const receipt = [...markers]
    .reverse()
    .find((row) => row.kind === 'event' && row.eventId !== null && row.matchPeriod !== null && row.matchSecond !== null);
  if (!receipt) return null;

  // Без записи пересчитываем позицию в эфире: организатор только стримит, и резать будут VOD.
  const timeline: VideoTimeline = receipt.recordingMs !== null ? 'recording' : 'streaming';
  const event = { id: receipt.eventId!, period: receipt.matchPeriod!, seconds: receipt.matchSecond! };

  return { eventType: receipt.eventType, timeline, position: resolveEventVideoPosition(event, markers, timeline) };
}

function reportError(comment: string, error: unknown) {
  console.error({ comment, error });
  supabase.overlayLogs.createError({ comment, message: error instanceof Error ? error.message : String(error) });
}
